import { useMutation, useQuery, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import axios from '../utils/axios.utils';

export interface UseGetOptions<TData = unknown> {
  url: string;
  params?: Record<string, unknown>;
  enabled?: boolean;
  queryKeyOverride?: unknown[];
}

export function useGet<TData = unknown>(
  options: UseGetOptions<TData>,
  queryOptions?: Omit<UseQueryOptions<AxiosResponse<TData>, Error>, 'queryKey' | 'queryFn'>,
) {
  const { url, params, enabled = true, queryKeyOverride } = options;
  const queryKey = queryKeyOverride ?? [url, params];

  return useQuery<AxiosResponse<TData>, Error>({
    queryKey,
    queryFn: async () => axios.get<TData>(url, { params }),
    enabled: !!url && enabled,
    ...queryOptions,
  });
}

export interface UseGetBlobOptions {
  url: string;
  enabled?: boolean;
  queryKeyOverride?: unknown[];
}

/** GET request with responseType: 'blob' (e.g. PDFs). **/
export function useGetBlob(
  options: UseGetBlobOptions,
  queryOptions?: Omit<UseQueryOptions<AxiosResponse<Blob>, Error>, 'queryKey' | 'queryFn'>,
) {
  const { url, enabled = true, queryKeyOverride } = options;
  const queryKey = queryKeyOverride ?? [url, 'blob'];

  return useQuery<AxiosResponse<Blob>, Error>({
    queryKey,
    queryFn: async () => axios.get(url, { responseType: 'blob' }),
    enabled: !!url && enabled,
    ...queryOptions,
  });
}

export interface MutationVariables {
  url: string;
  data?: object;
}

export function usePost<TData = unknown>(
  mutationOptions?: Omit<UseMutationOptions<AxiosResponse<TData>, unknown, MutationVariables>, 'mutationFn'>,
) {
  return useMutation<AxiosResponse<TData>, unknown, MutationVariables>({
    mutationFn: async ({ url, data }) => axios.post(url, data),
    ...mutationOptions,
  });
}

export function usePut<TData = unknown>() {
  // Here we have some good options to be handled by React Query:
  // - Provide custom request headers for authentication, content-type, etc.
  // - Pass and handle URL query params dynamically.
  // - Use cache to invalidate Integrate React Query's cache invalidation via `onSuccess` (e.g. refetchQueries, invalidateQueries).
  // - We can manipulate data (transforming request/response) before send it to the components
  // - Complete request lifecycle (onMutate, onSuccess, onError, onPending, onLoadings, etc).
  // - Allow manual retry configuration.
  // - Cache data (on GET requests) to use the same data on other pages, silint requests, invlidade chaches
  return useMutation<AxiosResponse<TData>, unknown, MutationVariables>({
    mutationFn: async ({ url, data }) => axios.put(url, data),
    // You can add the following options here for more features:
    // onSuccess: (data, variables, context) => { ... },
    // onError: (error, variables, context) => { ... },
    // onMutate: (variables) => { ... },
    // onSettled: (data, error, variables, context) => { ... },
    // mutationKey: ...
    // meta: ...
  });
}
